import React, { useEffect, useState } from "react";
import "./CustomersTable.css";
import SearchComponent from "../../components/search/SearchComp";
import { AddIcon, TelegramIcon } from "../../components/icons/Icons";
import InviteModal from "./InviteModal";
import ComposeMessageModal from "./ComposeMessageModal";
import BulkInviteModal from "./BulkInviteModal";
import NoItemFound from "../../components/NoItem/NoItem";
import axiosInstance from "../../axiosInstance";

interface Customer {
  customer_id: string;
  name: string;
  email: string;
  total_spent: number;
}

interface CustomerResponse {
  valid: boolean;
  data: {
    customers: Customer[];
    pagination: {
      total: number;
      page: number;
      limit: number;
    };
  };
}

const CustomersTable: React.FC = () => {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);

  const [currentPage, setCurrentPage] = useState(1);
  const [entriesPerPage, setEntriesPerPage] = useState<"all" | number>(10);
  const [totalCustomers, setTotalCustomers] = useState(0);

  const [searchQuery, setSearchQuery] = useState("");

  const [isFilterModalOpen, setFilterModalOpen] = useState(false);
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [isBulkInviteModalOpen, setIsBulkInviteModalOpen] = useState(false);

  const fetchCustomers = async () => {
    setLoading(true);
    try {
      const params: any = {
        page: currentPage,
        limit: entriesPerPage === "all" ? undefined : entriesPerPage,
        search: searchQuery || undefined,
      };

      const res = await axiosInstance.get<CustomerResponse>("/customers", {
        params,
      });

      if (res.data?.valid) {
        setCustomers(res.data.data.customers || []);
        setTotalCustomers(res.data.data.pagination?.total || 0);
      } else {
        setCustomers([]);
      }
    } catch {
      setCustomers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomers();
  }, [currentPage, entriesPerPage, searchQuery]);

  const totalPages =
    entriesPerPage === "all" ? 1 : Math.ceil(totalCustomers / Number(entriesPerPage));

  const handlePageChange = (page: number) => {
    if (page > 0 && page <= totalPages) setCurrentPage(page);
  };

  const handleEntriesChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setEntriesPerPage(value === "all" ? "all" : Number(value));
    setCurrentPage(1);
  };

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    setCurrentPage(1);
  };

  return (
    <div className="orders-table customers">
      <div className="orders__table__header">
        <div className="stock__flex__search customers">
          <SearchComponent onSearch={handleSearch} />

          <button onClick={() => setIsInviteModalOpen(true)}>
            <AddIcon />
            <p>Invite</p>
          </button>

          <button onClick={() => setIsBulkInviteModalOpen(true)}>
            <AddIcon />
            <p>Bulk Invite</p>
          </button>

          <button onClick={() => setFilterModalOpen(true)}>
            <TelegramIcon />
            <p>Message</p>
          </button>
        </div>
      </div>

      <table>
        <thead>
          <tr>
            <th>CUSTOMER NAME</th>
            <th>Email</th>
            <th>CUSTOMER ID</th>
            <th>TOTAL PURCHASE</th>
            <th>VIEW</th>
          </tr>
        </thead>

        <tbody>
          {loading ? (
            <tr>
              <td colSpan={5} style={{ textAlign: "center" }}>Loading...</td>
            </tr>
          ) : customers.length === 0 ? (
            <tr>
              <td colSpan={5}>
                <NoItemFound message="No customer found." />
              </td>
            </tr>
          ) : (
            customers.map((customer) => (
              <tr key={customer.customer_id} style={{ cursor: "pointer" }}>
                <td>{customer.name}</td>
                <td>{customer.email}</td>
                <td>{customer.customer_id}</td>
                <td>${customer.total_spent}</td>
                <td>
                  <button
                    className="view-button"
                    style={{
                      background: "#089B14",
                      color: "#fff",
                      border: "none",
                      borderRadius: "6px",
                      padding: "6px 18px",
                      fontWeight: 600,
                      cursor: "pointer",
                      textTransform: "capitalize",
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      window.location.href = `/purchase-log?id=${customer.customer_id}`;
                    }}
                  >
                    View
                  </button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>

      <div className="entries-control">
        <label>
          View
          <select
            className="entries-select"
            value={entriesPerPage}
            onChange={handleEntriesChange}
          >
            <option value="2">2</option>
            <option value="5">5</option>
            <option value="10">10</option>
            <option value="all">All</option>
          </select>
          Per Page
        </label>

        <div className="right__pagination">
          <p>
            Showing {(currentPage - 1) * (entriesPerPage === "all" ? totalCustomers : Number(entriesPerPage)) + 1}
            {" "}to{" "}
            {entriesPerPage === "all"
              ? totalCustomers
              : Math.min(currentPage * Number(entriesPerPage), totalCustomers)}{" "}
            of {totalCustomers} entries
          </p>

          {entriesPerPage !== "all" && totalPages > 1 && (
            <div className="pagination">
              <button
                className="pagination__button"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                Prev
              </button>

              {[...Array(totalPages)].map((_, i) => (
                <button
                  key={i}
                  className={`pagination__button ${currentPage === i + 1 ? "active" : ""}`}
                  onClick={() => handlePageChange(i + 1)}
                >
                  {i + 1}
                </button>
              ))}

              <button
                className="pagination__button"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                Next
              </button>
            </div>
          )}
        </div>
      </div>

      <ComposeMessageModal
        isOpen={isFilterModalOpen}
        onClose={() => setFilterModalOpen(false)}
        onFilter={() => {}}
      />

      <InviteModal
        isOpen={isInviteModalOpen}
        onClose={() => setIsInviteModalOpen(false)}
        onFilter={() => {}}
      />

      <BulkInviteModal
        isOpen={isBulkInviteModalOpen}
        onClose={() => setIsBulkInviteModalOpen(false)}
        onFilter={() => {}}
      />
    </div>
  );
};

export default CustomersTable;
